import type { JSX } from 'react';
import type { HTMLAttributes, ReactNode, ButtonHTMLAttributes } from "react";

export interface ToolbarProps extends HTMLAttributes<HTMLDivElement> {
  children?: ReactNode;
}

/**
 * <Toolbar> — horizontal action strip.
 *
 * Used on the topbar right-side and above list pages. Composes
 * Button / Toolbar.Seg / search. Adds the ARIA role but stays flat
 * visually (token-backed `.cp-toolbar`).
 */
export function Toolbar({ children, className, ...rest }: ToolbarProps): JSX.Element {
  return (
    <div
      role="toolbar"
      className={["cp-toolbar", className].filter(Boolean).join(" ")}
      {...rest}
    >
      {children}
    </div>
  );
}

export interface ToolbarSegProps extends HTMLAttributes<HTMLDivElement> {
  "aria-label"?: string;
}

/** Segmented control container for Toolbar. */
function Seg({ children, className, ...rest }: ToolbarSegProps): JSX.Element {
  return (
    <div
      role="group"
      className={["cp-toolbar-seg", className].filter(Boolean).join(" ")}
      {...rest}
    >
      {children}
    </div>
  );
}

export interface ToolbarSegButtonProps
  extends ButtonHTMLAttributes<HTMLButtonElement> {
  on?: boolean;
}

/** Button inside a Toolbar.Seg. Consumer drives `on` state. */
function SegButton({
  on = false,
  className,
  children,
  type = "button",
  ...rest
}: ToolbarSegButtonProps): JSX.Element {
  return (
    <button
      type={type}
      aria-pressed={on}
      className={[on && "on", className].filter(Boolean).join(" ")}
      {...rest}
    >
      {children}
    </button>
  );
}

/** Flex spacer — pushes subsequent items to the right. */
function Spacer(): JSX.Element {
  return <span className="cp-toolbar-spacer" aria-hidden="true" />;
}

Toolbar.Seg = Seg;
Toolbar.SegButton = SegButton;
Toolbar.Spacer = Spacer;
